import { catchError, from, map, Observable, switchMap, tap } from 'rxjs';
import { AxiosError, AxiosResponse } from 'axios';
import { upsertEntities } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';
import { GridSortItem } from '@mui/x-data-grid-pro';

import { documentsStore } from '@store/documents/documents.store';
import { Document, DocumentsFilters, DocumentsPagination, ManageDocument } from '@store/documents/documents.model';
import { Pagination } from '@store/common/pagination.model';
import { TableFilter } from '@store/common/tableFilter.model';
import { getDocumentsDataSource } from '@store/documents/documents.requests';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';

export class DocumentsService {
  store = documentsStore;

  resetStore = () => this.store.reset();

  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (clientId?: string, supplierId?: string, clientUnitId?: string, supplierUnitId?: string) => this.store.update((state) => ({
    ...state,
    filters: { search: '', tableFilters: DocumentsFilters.initTableFilters(clientId, supplierId, clientUnitId, supplierUnitId) },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getDocuments = (): Observable<DocumentsPagination> => {
    const { filters: { documentKind, importDate, search, tableFilters }, sort, tablePagination } = this.store.getValue();

    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETDocuments(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        documentKind: documentKind || undefined,
        importDate: importDate?.toISOString() || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<DocumentsPagination>) => {
          return response.data;
        }),
        tap((documentsPagination) => {
          const paginationData = Pagination.toPaginationData(documentsPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(documentsPagination.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, documentsPagination.data.map((document) => document.id)),
            getDocumentsDataSource.setSuccess(),
          );
        }),
        getDocumentsDataSource.trackRequestStatus(),
      );
  };

  createDocument = (document: ManageDocument): Observable<Document> => {
    return from(APIAxios({
      ...APIRoutes.POSTDocument(),
      data: Document.toFormData(document),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Document>) => {
          return response.data;
        }),
        switchMap((doc) => this.getDocuments().pipe(map(() => doc))),
      );
  };

  updateDocument = (documentId: string | number, document: ManageDocument): Observable<Document> => {
    return from(APIAxios({
      ...APIRoutes.PUTDocument(documentId),
      data: {
        documentKind: document.documentKind,
        organisationType: document.organisationType,
        organisation: document.organisation?.id,
        organisationUnit: document.organisationUnit?.id,
        startValidity: document.startValidity?.toISOString() ?? undefined,
        endValidity: document.endValidity?.toISOString() ?? undefined,
        amount: document.amount ?? undefined,
        reference: document.reference ?? undefined,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Document>) => {
          return response.data;
        }),
        switchMap((doc) => this.getDocuments().pipe(map(() => doc))),
      );
  };

  getSelectedDocument = (documentId: number | string): Observable<Document> => {
    return from(APIAxios(APIRoutes.GETDocumentById(documentId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Document>) => {
          return response.data;
        }),
      );
  };

  deleteDocument = (documentId: number | string): Observable<DocumentsPagination> => {
    return from(APIAxios(APIRoutes.DELETEDocument(documentId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getDocuments()),
      );
  };
}

export const documentsService = new DocumentsService();
